import { useState, useEffect, useCallback, useMemo } from "react";
import type { ISalaryUserBackup } from "@/components/common/SalaryConfigTable";

export interface ISalaryHeadInfo {
  id: string;
  name: string;
  type: string;
}

/**
 * Helper to check if a head name represents Gross Salary
 */
const isGrossSalaryHead = (name: string): boolean => {
  const lower = name?.toLowerCase()?.trim() || "";
  return (
    lower.includes("gross salary") ||
    lower.includes("gross-salary") ||
    lower.includes("gross_salary") ||
    lower === "gross pay" ||
    lower === "gross"
  );
};

/**
 * Helper to check if a head name represents Net Salary
 */
const isNetSalaryHead = (name: string): boolean => {
  const lower = name?.toLowerCase()?.trim() || "";
  return (
    lower.includes("net salary") ||
    lower.includes("net-salary") ||
    lower.includes("net_salary") ||
    lower === "net pay"
  );
};

/**
 * High-Performance Custom Hook to manage salary configuration table inputs & dynamic calculations.
 * Optimized with O(1) Hash Maps and pre-indexed head lookups.
 */
export const useSalaryConfigTable = (
  data: ISalaryUserBackup[],
  uniqueSalaryHeads: ISalaryHeadInfo[],
) => {
  // ── Input State Map: { [userId]: { [branchSalaryHeadId]: amount } }
  const [inputs, setInputs] = useState<
    Record<string, Record<string, number | string>>
  >({});

  // Fast initial input sync with auto-calculation of Gross & Net Salary on initial fetch
  useEffect(() => {
    if (!data || data.length === 0) {
      setInputs({});
      return;
    }
    const map: Record<string, Record<string, number | string>> = {};

    const netHead = uniqueSalaryHeads.find((h) => isNetSalaryHead(h.name));
    const grossHead = uniqueSalaryHeads.find((h) => isGrossSalaryHead(h.name));

    const isBasicHead = (name: string): boolean => {
      const lower = name?.toLowerCase()?.trim() || "";
      return lower.includes("basic");
    };

    data.forEach((usr) => {
      const userInputs: Record<string, number | string> = {};

      uniqueSalaryHeads.forEach((head) => {
        const headNameLower = head.name?.toLowerCase()?.trim() || "";
        const isHeadBasic = isBasicHead(head.name);

        // Multi-level matching: 1) ID match 2) Name match 3) Semantic Basic match
        const matchedSalary = usr.salaries?.find((sal) => {
          const salHeadId = String(sal.branchSalaryHeadId || sal.branchSalaryHead?.id || "");
          const salInnerHeadId = String(sal.branchSalaryHead?.salaryHeadId || (sal as any).salaryHeadId || "");
          const salName = (
            sal.salaryName ||
            sal.branchSalaryHead?.salaryName ||
            sal.branchSalaryHead?.salaryHead?.name ||
            ""
          ).toLowerCase().trim();

          // 1. Direct ID match
          if (salHeadId && salHeadId === String(head.id)) return true;
          if (salInnerHeadId && salInnerHeadId === String(head.id)) return true;

          // 2. Name match (case-insensitive)
          if (salName && headNameLower && (salName === headNameLower || salName.includes(headNameLower) || headNameLower.includes(salName))) {
            return true;
          }

          // 3. Basic head fallback match
          if (isHeadBasic && isBasicHead(salName)) {
            return true;
          }

          return false;
        });

        const rawVal = matchedSalary?.amount;
        const val = rawVal !== null && rawVal !== undefined && rawVal !== "" ? Number(rawVal) : 0;
        userInputs[head.id] = isNaN(val) ? 0 : val;
      });

      let totalEarnings = 0;
      let totalDeductions = 0;

      uniqueSalaryHeads.forEach((head) => {
        const val = Number(userInputs[head.id]) || 0;
        const isEarning = head.type === "earning";
        const isNet = isNetSalaryHead(head.name);
        const isGross = isGrossSalaryHead(head.name);

        if (isEarning) {
          if (!isNet && !isGross) {
            totalEarnings += val;
          }
        } else {
          totalDeductions += val;
        }
      });

      const formatToTwoDecimals = (val: number): number => {
        return Number(Math.max(0, val).toFixed(2));
      };

      const calculatedGrossSalary = formatToTwoDecimals(totalEarnings);
      const calculatedNetSalary = formatToTwoDecimals(
        totalEarnings - totalDeductions
      );

      if (grossHead) {
        const existingGross = Number(userInputs[grossHead.id]) || 0;
        userInputs[grossHead.id] = existingGross > 0 ? formatToTwoDecimals(existingGross) : calculatedGrossSalary;
      }

      if (netHead) {
        const existingNet = Number(userInputs[netHead.id]) || 0;
        userInputs[netHead.id] = existingNet > 0 ? formatToTwoDecimals(existingNet) : calculatedNetSalary;
      }

      map[usr.id] = userInputs;
    });

    setInputs(map);
  }, [data, uniqueSalaryHeads]);

  // Pre-index special Net & Gross salary heads to eliminate runtime array searches
  const { netSalaryHead, grossSalaryHead } = useMemo(() => {
    const net = uniqueSalaryHeads.find((h) => isNetSalaryHead(h.name));
    const gross = uniqueSalaryHeads.find((h) => isGrossSalaryHead(h.name));
    return { netSalaryHead: net, grossSalaryHead: gross };
  }, [uniqueSalaryHeads]);

  /**
   * Handle input change for a user's salary head and auto-recalculate Net & Gross Salary
   */
  const handleInputChange = useCallback(
    (userId: string, headId: string, value: string) => {
      // 1. Remove non-numeric characters except dot
      let clean = String(value).replace(/[^0-9.]/g, "");

      // 2. Allow only single dot
      const dotIndex = clean.indexOf(".");
      if (dotIndex !== -1) {
        const beforeDot = clean.slice(0, dotIndex);
        const afterDot = clean.slice(dotIndex + 1).replace(/\./g, "");
        clean = beforeDot + "." + afterDot;
      }

      // 3. Limit to maximum 7 digits before decimal point
      const parts = clean.split(".");
      if (parts[0].length > 7) {
        parts[0] = parts[0].slice(0, 7);
        clean = parts.length > 1 ? parts[0] + "." + parts[1] : parts[0];
      }

      // 4. Limit to maximum 2 digits after decimal point
      if (parts.length > 1 && parts[1].length > 2) {
        clean = parts[0] + "." + parts[1].slice(0, 2);
      }

      setInputs((prev) => {
        const userPrevInputs = prev[userId] || {};
        const updatedUserInputs: Record<string, number | string> = {
          ...userPrevInputs,
          [headId]: clean,
        };

        // Calculate Total Earnings & Total Deductions
        let totalEarnings = 0;
        let totalDeductions = 0;

        uniqueSalaryHeads.forEach((head) => {
          const val = Number(updatedUserInputs[head.id]) || 0;
          const isEarning = head.type === "earning";
          const isNet = isNetSalaryHead(head.name);
          const isGross = isGrossSalaryHead(head.name);

          if (isEarning) {
            if (!isNet && !isGross) {
              totalEarnings += val;
            }
          } else {
            totalDeductions += val;
          }
        });

        const formatToTwoDecimals = (val: number): number => {
          return Number(Math.max(0, val).toFixed(2));
        };

        const calculatedGrossSalary = formatToTwoDecimals(totalEarnings);
        const calculatedNetSalary = formatToTwoDecimals(
          totalEarnings - totalDeductions
        );

        if (grossSalaryHead) {
          updatedUserInputs[grossSalaryHead.id] = calculatedGrossSalary;
        }

        if (netSalaryHead) {
          updatedUserInputs[netSalaryHead.id] = calculatedNetSalary;
        }

        return {
          ...prev,
          [userId]: updatedUserInputs,
        };
      });
    },
    [uniqueSalaryHeads, netSalaryHead, grossSalaryHead],
  );

  return {
    inputs,
    setInputs,
    handleInputChange,
  };
};
